import React, { useMemo } from 'react';
import { Platform, StyleSheet, View } from 'react-native';
import { BlurView } from 'expo-blur';
import PropTypes from 'prop-types';
import useResponsiveness from '@/hooks/useResponsiveness';
import { useTheme } from '@/context/ThemeContext';

const withAlpha = (color, alpha, fallback) => {
  if (typeof color !== 'string') return fallback;
  const normalized = color.trim();
  if (/^#[0-9a-f]{6}$/i.test(normalized)) {
    const value = Math.round(alpha * 255).toString(16).padStart(2, '0');
    return `${normalized}${value}`;
  }
  if (/^#[0-9a-f]{3}$/i.test(normalized)) {
    const expanded = normalized.slice(1).split('').map(char => char + char).join('');
    const value = Math.round(alpha * 255).toString(16).padStart(2, '0');
    return `#${expanded}${value}`;
  }
  return fallback;
};

const createStyles = (colors, isXS, isGapped, isDark) =>StyleSheet.create({
  container: {
    flexDirection: 'row',
    gap: isGapped ? 4 : isXS ? 0 : 1,
    alignItems: 'center',
  },
  mobileGlassContainer: {
    minHeight: 36,
    paddingHorizontal: 3,
    paddingVertical: 3,
    borderRadius: 19,
    overflow: 'hidden',
    position: 'relative',
    backgroundColor: withAlpha(
      colors.solid_color_block,
      isDark ? 0.44 : 0.54,
      isDark ? 'rgba(44, 44, 48, 0.44)' : 'rgba(255, 255, 255, 0.54)',
    ),
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: withAlpha(
      colors.border_color_secondary,
      isDark ? 0.52 : 0.42,
      isDark ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.10)',
    ),
    ...(Platform.OS === 'web' ? {
      backdropFilter: 'blur(22px) saturate(1.8)',
      WebkitBackdropFilter: 'blur(22px) saturate(1.8)',
      boxShadow: isDark ? '0 8px 22px rgba(0, 0, 0, 0.18)' : '0 8px 22px rgba(0, 0, 0, 0.10)',
    } : {}),
  },
  nativeGlassBlur: {
    ...StyleSheet.absoluteFillObject,
    borderRadius: 19,
  },
  rightPadding: {
    paddingRight: 10,
  },
  leftPadding: {
    paddingLeft: 10,
  },
  mobileRightOffset: {
    marginRight: 10,
  },
  mobileLeftOffset: {
    marginLeft: 10,
  },
});

const NavigationBarButtonContainer = ({ children, rightPadding, leftPadding, isGapped, glass = false, style }) => {
  if (!children) {
    return null;
  }
  const { isXS } = useResponsiveness();
  const { colors, theme } = useTheme();
  const isMobileGlass = glass && (Platform.OS === 'ios' || Platform.OS === 'android' || (Platform.OS === 'web' && isXS));
  const isNativeMobileGlass = isMobileGlass && (Platform.OS === 'ios' || Platform.OS === 'android');
  const isDark = theme !== 'light';
  const styles = useMemo(() => createStyles(colors, isXS, isGapped, isDark), [colors, isXS, isGapped, isDark]);

  return (
    <View 
      style={[
        styles.container,
        isMobileGlass && styles.mobileGlassContainer,
        rightPadding && (isMobileGlass ? styles.mobileRightOffset : styles.rightPadding),
        leftPadding && (isMobileGlass ? styles.mobileLeftOffset : styles.leftPadding),
        style,
        // isGapped && styles.gapped,
      ]}
      accessibilityRole="toolbar"
      accessibilityLabel="Navigation toolbar"
    >
      {isNativeMobileGlass && (
        <BlurView
          pointerEvents="none"
          intensity={Platform.OS === 'ios' ? 28 : 18}
          tint={isDark ? 'dark' : 'light'}
          experimentalBlurMethod={Platform.OS === 'android' ? 'dimezisBlurView' : undefined}
          style={styles.nativeGlassBlur}
        />
      )}
      {children}
    </View>
  );
};

NavigationBarButtonContainer.propTypes = {
  children: PropTypes.node,
  rightPadding: PropTypes.bool,
  leftPadding: PropTypes.bool,
  glass: PropTypes.bool,
  style: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
};

NavigationBarButtonContainer.displayName = 'NavigationBarButtonContainer';

export default React.memo(NavigationBarButtonContainer);
